/**
 * EAA-1670: authSlice/authSaga retired onto Zustand (Phase 7 state migration,
 * last of the high-stakes tier — see
 * docs/plans/fe-restructure/phase-7-state-migration.md). This store holds
 * only the STATE shape (mirroring the old `AuthState` reducers 1:1); the
 * actual async orchestration (login/logout/checkAuth/embedBootstrap, plus the
 * `auth:logout`/`auth:embed-invalid` window-event bridge from the api client)
 * lives in `@/data/auth/authOperations.ts`, which calls these setters.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { User, AuthMode } from '@/services/auth.service';

export interface LoginFieldErrors {
    tab: 'staff' | 'tenant';
    details: Record<string, string[]>;
}

export interface AuthState {
    user: User | null;
    authMode: AuthMode;
    isAuthenticated: boolean;
    isLoading: boolean;
    isCheckingAuth: boolean;
    error: string | null;
    loginFieldErrors: LoginFieldErrors | null;
}

export type LoginFailurePayload =
    | string
    | { message: string; tab: 'staff' | 'tenant'; details?: Record<string, string[]> };

interface AuthStoreActions {
    loginStart: () => void;
    loginSuccess: (user: User, authMode?: AuthMode) => void;
    loginFailure: (payload: LoginFailurePayload) => void;
    logoutStart: () => void;
    logoutSuccess: () => void;
    logoutFailure: (message: string) => void;
    embedBootstrapStart: () => void;
    embedBootstrapSuccess: (user: User) => void;
    embedBootstrapFailure: (message: string) => void;
    embedSessionEnded: (message?: string) => void;
    checkAuthStart: () => void;
    checkAuthSuccess: (user: User, authMode: AuthMode) => void;
    checkAuthFailure: () => void;
    clearError: () => void;
    clearLoginFieldErrors: () => void;
}

const initialState: AuthState = {
    user: null,
    authMode: 'staff',
    isAuthenticated: false,
    isLoading: false,
    isCheckingAuth: true,
    error: null,
    loginFieldErrors: null,
};

export const useAuthStore = create<AuthState & AuthStoreActions>()(
    devtools(
        (set) => ({
            ...initialState,
            loginStart: () =>
                set(
                    { isLoading: true, error: null, loginFieldErrors: null },
                    false,
                    'auth/loginStart'
                ),
            loginSuccess: (user, authMode) =>
                set(
                    {
                        isLoading: false,
                        isAuthenticated: true,
                        user,
                        authMode: authMode ?? 'staff',
                        error: null,
                        loginFieldErrors: null,
                    },
                    false,
                    'auth/loginSuccess'
                ),
            loginFailure: (payload) =>
                set(
                    () => {
                        if (typeof payload === 'string') {
                            return {
                                isLoading: false,
                                isAuthenticated: false,
                                user: null,
                                error: payload,
                                loginFieldErrors: null,
                            };
                        }
                        const { message, tab, details } = payload;
                        const hasDetails = Boolean(details && Object.keys(details).length > 0);
                        return {
                            isLoading: false,
                            isAuthenticated: false,
                            user: null,
                            loginFieldErrors: hasDetails && details ? { tab, details } : null,
                            error: hasDetails ? null : message,
                        };
                    },
                    false,
                    'auth/loginFailure'
                ),
            logoutStart: () => set({ isLoading: true }, false, 'auth/logoutStart'),
            logoutSuccess: () =>
                set(
                    {
                        isLoading: false,
                        isAuthenticated: false,
                        user: null,
                        authMode: 'staff',
                        error: null,
                    },
                    false,
                    'auth/logoutSuccess'
                ),
            logoutFailure: (message) =>
                set(
                    { isLoading: false, error: message, isAuthenticated: false, user: null },
                    false,
                    'auth/logoutFailure'
                ),
            embedBootstrapStart: () =>
                set(
                    { isLoading: true, error: null, isCheckingAuth: false },
                    false,
                    'auth/embedBootstrapStart'
                ),
            embedBootstrapSuccess: (user) =>
                set(
                    {
                        isLoading: false,
                        isAuthenticated: true,
                        user,
                        authMode: 'embed_api',
                        error: null,
                    },
                    false,
                    'auth/embedBootstrapSuccess'
                ),
            embedBootstrapFailure: (message) =>
                set(
                    {
                        isLoading: false,
                        isAuthenticated: false,
                        user: null,
                        authMode: 'staff',
                        error: message,
                    },
                    false,
                    'auth/embedBootstrapFailure'
                ),
            embedSessionEnded: (message) =>
                set(
                    {
                        isLoading: false,
                        isAuthenticated: false,
                        user: null,
                        authMode: 'staff',
                        error: message ?? null,
                    },
                    false,
                    'auth/embedSessionEnded'
                ),
            checkAuthStart: () => set({ isCheckingAuth: true }, false, 'auth/checkAuthStart'),
            checkAuthSuccess: (user, authMode) =>
                set(
                    { isCheckingAuth: false, isAuthenticated: true, user, authMode },
                    false,
                    'auth/checkAuthSuccess'
                ),
            checkAuthFailure: () =>
                set(
                    {
                        isCheckingAuth: false,
                        isAuthenticated: false,
                        user: null,
                        authMode: 'staff',
                    },
                    false,
                    'auth/checkAuthFailure'
                ),
            clearError: () => set({ error: null }, false, 'auth/clearError'),
            clearLoginFieldErrors: () =>
                set({ loginFieldErrors: null }, false, 'auth/clearLoginFieldErrors'),
        }),
        { name: 'auth' }
    )
);
